import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PLAN_CARDS } from '@/config/planFeatures';

/* THE PLAN CARDS, rendered. Lifted verbatim from UxAuditorPage.js (owner,
   2026-10-08) so the homepage shows the same three plans with the same names,
   bullets and buttons: the homepage had its own two-card copy ("Free Audit",
   "Complete Report") and drifted, exactly as /ux-auditor/ had before
   PLAN_CARDS. The page owns the grid and the checkout popup; `onCheckout`
   opens it from the highlighted plan's button. */
export default function PlanCards({ onCheckout }) {
  const { login, user } = useAuth();
  const setShowCheckout = () => onCheckout();
  return (
    <>
      {PLAN_CARDS.map((plan) => (
          <div key={plan.name} className={`uxa-price-card${plan.highlighted ? ' uxa-price-card--dark' : ''} relative flex flex-col h-full`} style={{ textAlign: 'left' }} data-testid={`pricing-card-${plan.name.toLowerCase()}`}>
            {/* The "Best Value" badge was here until 2026-08-26. It is a
                claim about what other people bought, and nothing counts
                that -- the same reason the artboard's "Most Popular" was
                dropped from the homepage card. It survived this long only
                because this file was outside the count-claim corpus, which
                is the gap the same commit closes; the guard fails on the
                badge the moment the file is swept.

                The card does not need it. "Up to 3 pages of the same site"
                against "1 page" is the difference a badge was standing in
                for, and that one is a fact about the product.

                `highlighted` is untouched: it still carries the dark card
                skin and the primary button. Only the claim is gone. */}
            <div className="mb-6">
              <h3 className="uxa-h3 mb-0.5" style={{ color: plan.highlighted ? 'white' : 'var(--ink-1)' }}>{plan.name}</h3>
              <p className="text-sm" style={{ color: plan.highlighted ? 'rgba(255,255,255,0.6)' : 'var(--ink-2)' }}>{plan.type}</p>
              {plan.badge && <span className="uxa-price-badge" style={{ display: 'inline-block', marginTop: 'var(--space-3)' }}>{plan.badge}</span>}
            </div>
            <div className="mb-6">
              <span className="font-bold text-3xl" style={{ color: plan.highlighted ? 'white' : 'var(--ink-1)', letterSpacing: '-0.04em' }}>{plan.price}</span>
              <span className="text-sm" style={{ color: plan.highlighted ? 'rgba(255,255,255,0.6)' : 'var(--ink-2)' }}>{plan.period}</span>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: plan.highlighted ? 'rgba(255,255,255,0.5)' : 'var(--ink-2)' }} />
                  <span className="text-sm" style={{ color: plan.highlighted ? 'rgba(255,255,255,0.8)' : 'var(--ink-2)' }}>{f}</span>
                </li>
              ))}
            </ul>
            {/* PHASE F. The three plan actions are the button system now:
                the highlighted plan takes the PRIMARY skin, the others the
                SECONDARY. They were a --surface-panel fill at 1.04:1
                against the canvas, rescued in CTA-3 by adding the card's
                border; the secondary skin carries that border as part of
                the skin, so the local fix comes out with it. */}
            {plan.contact ? (
              <Link
                to="/contact/"
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                className="uxa-submit uxa-submit--block uxa-submit--secondary"
              >
                Get in touch
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            ) : plan.highlighted ? (
              <button
                onClick={() => setShowCheckout(true)}
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                /* --on-dark: this branch only renders on the HIGHLIGHTED
                   plan, whose card ground is --ink-strong. Without it the
                   button is #282628 on #282628 — 1.00:1, the original
                   defect, reproduced by the phase that generalised the
                   guard against it. The guard could not see this one: the
                   card's ground is a TERNARY, and the resolver only read a
                   literal. It reads ternaries now. */
                className="uxa-submit uxa-submit--block uxa-submit--on-dark"
              >
                {plan.highlighted ? 'Get a full audit' : 'Start free'}
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            ) : user ? (
              <Link
                to="/audit/new"
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                className="uxa-submit uxa-submit--block uxa-submit--secondary"
              >
                {plan.highlighted ? 'Get a full audit' : 'Start free'}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            ) : (
              <button
                onClick={login}
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                className="uxa-submit uxa-submit--block uxa-submit--secondary"
              >
                {plan.highlighted ? 'Get a full audit' : 'Start free'}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
            )}
          </div>
      ))}
    </>
  );
}
